import {
  Facebook,
  Globe,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Star,
  Youtube,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  useGetIdentity,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { EditButton } from "@/components/admin/edit-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { ShowButton } from "@/components/admin/show-button";
import { TextField } from "@/components/admin/text-field";
import { UrlField } from "@/components/admin/url-field";

import { findOriginLabel } from "@/lib/originChannels";
import { formatLocalizedDate } from "../misc/RelativeDate";
import { AsideSection } from "../misc/AsideSection";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { useGetSalesName } from "../sales/useGetSalesName";

interface CompanyAsideProps {
  link?: string;
}

export const CompanyAside = ({ link = "edit" }: CompanyAsideProps) => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  if (!record) return null;

  return (
    <div className="hidden sm:block w-92 min-w-92 space-y-4">
      <div className="flex flex-row space-x-1">
        {link === "edit" ? (
          <EditButton label={translate("resources.companies.action.edit")} />
        ) : (
          <ShowButton label={translate("resources.companies.action.show")} />
        )}
      </div>

      <CompanyInfo record={record} />
      <AddressInfo record={record} />
      <ContextInfo record={record} />
      <AdditionalInfo record={record} />

      {link !== "edit" && (
        <div className="mt-6 pt-6 border-t hidden sm:flex flex-col gap-2 items-start">
          <DeleteButton
            className="h-6 cursor-pointer hover:bg-destructive/10! text-destructive! border-destructive! focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40"
            size="sm"
          />
        </div>
      )}
    </div>
  );
};

function SocialLink({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: ReactNode;
}) {
  const url = href.startsWith("http") ? href : `https://${href}`;
  return (
    <div className="flex flex-row items-center gap-1 min-h-[24px]">
      {icon}
      <a
        className="underline hover:no-underline break-all"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={href}
      >
        {label}
      </a>
    </div>
  );
}

export const CompanyInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const hasContact =
    record.website ||
    record.linkedin_url ||
    record.instagram ||
    record.tiktok ||
    record.facebook ||
    record.youtube ||
    record.maps_url ||
    record.email ||
    record.phone_number ||
    record.rating != null;

  if (!hasContact) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.contact")}
    >
      {record.website && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Globe className="w-4 h-4" aria-hidden="true" />
          <UrlField
            source="website"
            target="_blank"
            rel="noopener"
            content={record.website
              .replace("http://", "")
              .replace("https://", "")}
          />
        </div>
      )}
      {record.linkedin_url && (
        <SocialLink
          href={record.linkedin_url}
          label="LinkedIn"
          icon={<Linkedin className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.instagram && (
        <SocialLink
          href={record.instagram}
          label="Instagram"
          icon={<Instagram className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.tiktok && (
        <SocialLink
          href={record.tiktok}
          label="TikTok"
          icon={<Globe className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.facebook && (
        <SocialLink
          href={record.facebook}
          label="Facebook"
          icon={<Facebook className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.youtube && (
        <SocialLink
          href={record.youtube}
          label="YouTube"
          icon={<Youtube className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.maps_url && (
        <SocialLink
          href={record.maps_url}
          label={translate("resources.companies.fields.maps_url", {
            _: "Google Maps",
          })}
          icon={<MapPin className="w-4 h-4" aria-hidden="true" />}
        />
      )}
      {record.email && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Mail className="w-4 h-4" aria-hidden="true" />
          <a
            className="underline hover:no-underline"
            href={`mailto:${record.email}`}
          >
            {record.email}
          </a>
        </div>
      )}
      {record.phone_number && (
        <div className="flex flex-row items-center gap-1 min-h-[24px]">
          <Phone className="w-4 h-4" aria-hidden="true" />
          <TextField source="phone_number" />
        </div>
      )}
      {record.rating != null && (
        <div className="flex flex-row items-center gap-1 min-h-[24px] text-sm">
          <Star className="w-4 h-4" aria-hidden="true" />
          {record.rating}
          {record.reviews_count != null
            ? ` · ${record.reviews_count} reseñas`
            : null}
        </div>
      )}
    </AsideSection>
  );
};

export const ContextInfo = ({ record }: { record: Company }) => {
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();
  const sector = companySectors.find((item) => item.value === record.sector);
  if (!record.origin && !sector && !record.list_name && !record.revenue) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.context")}
    >
      {record.list_name && (
        <span>
          {translate("resources.companies.folders.title", { _: "Carpeta" })}:{" "}
          {record.list_name}
        </span>
      )}
      {record.origin && (
        <span>
          {translate("resources.deals.fields.origin", { _: "Canal de origen" })}
          : {findOriginLabel(record.origin)}
        </span>
      )}
      {sector?.label && (
        <span>
          {translate("resources.companies.fields.sector")}: {sector.label}
        </span>
      )}
      {record.revenue && (
        <span>
          {translate("resources.companies.fields.revenue")}:{" "}
          <TextField source="revenue" />
        </span>
      )}
      {record.tax_identifier && (
        <span>
          {translate("resources.companies.fields.tax_identifier")}:{" "}
          <TextField source="tax_identifier" />
        </span>
      )}
    </AsideSection>
  );
};

export const AddressInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  if (
    !record.address &&
    !record.city &&
    !record.zipcode &&
    !record.state_abbr
  ) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.address")}
      noGap
    >
      <TextField source="address" />
      <TextField source="city" />
      <TextField source="zipcode" />
      <TextField source="state_abbr" />
      <TextField source="country" />
    </AsideSection>
  );
};

export const AdditionalInfo = ({ record }: { record: Company }) => {
  const translate = useTranslate();
  const [locale = "en"] = useLocaleState();
  const { identity } = useGetIdentity();
  const isCurrentUser = record.sales_id === identity?.id;
  const salesName = useGetSalesName(record.sales_id, {
    enabled: !isCurrentUser,
  });
  const bulletLines = (record.description ?? "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!record.created_at && !record.sales_id && bulletLines.length === 0) {
    return null;
  }

  return (
    <AsideSection
      title={translate("resources.companies.field_categories.additional_info")}
    >
      {bulletLines.length > 0 ? (
        <ul className="list-disc pl-4 space-y-1 text-sm">
          {bulletLines.map((line) => (
            <li key={line}>{line.replace(/^- /, "")}</li>
          ))}
        </ul>
      ) : null}
      {record.sales_id !== null && (
        <div className="inline-flex text-sm text-muted-foreground mb-1">
          {translate(
            isCurrentUser
              ? "resources.companies.followed_by_you"
              : "resources.companies.followed_by",
            { name: salesName },
          )}
        </div>
      )}
      {record.created_at && (
        <p className="text-sm text-muted-foreground mb-1">
          {translate("resources.companies.added_on", {
            date: formatLocalizedDate(record.created_at, locale),
          })}{" "}
        </p>
      )}
    </AsideSection>
  );
};
